import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Layout from 'containers/Layout'
import moment from 'moment'
import { find, get, isArray, toUpper } from 'lodash'
import apiGet from 'lib/network/apiGet'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import Table from 'components/pageComponents/Table'
import LoadingIcon from 'components/atoms/LoadingIcon'
import EmptyState from 'components/pageComponents/EmptyState'
import { IoCopyOutline, IoPencil, IoSearch, IoTrashBin } from 'react-icons/io5'
import LanguageSwitcher from 'components/atoms/LanguageSwitcher'
import { useSelector } from 'react-redux'
import PageFooter from 'components/paginationComponent'
import classNames from 'classnames'
import useModal from 'hooks/useModal'
import AddMakeModal from './modals/AddMakeModal'
import AddCityModal from './modals/AddCityModal'
import AddCountryModal from './modals/AddCountryModal'
import AddModelModal from './modals/AddModelModal'
import AddVehicleModelProfileModal from './modals/AddVehicleModelProfileModal'
import AddColorModal from './modals/AddColorModal'
import notify from 'components/atoms/Notify'
import apiDelete from 'lib/network/apiDelete'
import Selection from 'components/molecules/Selection'
import apiPost from 'lib/network/apiPost'
import apiPut from 'lib/network/apiPut'
import * as yup from 'yup'
import AddFuelPolicyModal from './modals/AddFuelPolicyModal'
import AddInsuranceModal from './modals/AddInsuranceModal'
import AddMarketSegmentModal from './modals/AddMarketSegmentModal'
import AddDriveWheelModal from './modals/AddDriveWheelModal'
import AddNoteModal from './modals/AddNoteModal'
import Switch from 'components/atoms/Switch'
import CarJson from './modals/CarJsonModal'
import PointOfInterestPage from './PointOfInterestPage'
import RefundEngine from 'pages/policy/refundPolicy'
import { startEndTruncate } from 'utils'

const LISTING_MIN_MODEL_YEAR = 2022

const MasterData = () => {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.language === 'ar'
  const [currentTab, setCurrentTab] = useState(1)
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({})
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState({})

  const [makes, setMakes] = useState([])
  const [models, setModels] = useState([])
  const [colors, setColors] = useState([])
  const [fuelPolicies, setFuelPolicy] = useState([])
  const [insurances, setInsurances] = useState([])
  const [marketSegments, setMarketSegments] = useState([])
  const [driveWheels, setDriveWheels] = useState([])
  const [notes, setNotes] = useState([])
  const [refundPolicyContents, setRefundPolicyContents] = useState([])
  const [appBanners, setAppBanners] = useState([])
  const [bookingCountries, setBookingCountries] = useState([])
  const [cities, setCities] = useState([])
  const [countries, setCountries] = useState([])
  const [vehicleModelProfiles, setVehicleModelProfiles] = useState([])

  // Add state for car JSON data
  const [carJsonData, setCarJsonData] = useState([])
  const [carJsonLoading, setCarJsonLoading] = useState(false)

  const [configType, setConfigType] = useState('')
  const [configValue, setConfigValue] = useState('')
  const [fromTime, setFromTime] = useState('')
  const [toTime, setToTime] = useState('')
  const [status, setStatus] = useState(false)
  const [bookingConfirmationTime, setBookingConfirmationTime] = useState(0)
  const [checkoutRejectAfterConfirmationExpiry, setCheckoutRejectAfterConfirmationExpiry] = useState(false)
  const [hideOlderModelYears, setHideOlderModelYears] = useState(false)
  const [configExists, setConfigExists] = useState(false)
  const [previousConfigType, setPreviousConfigType] = useState('')
  const [validationErrors, setValidationErrors] = useState({})

  const [selectedItemId, setSelectedItemId] = useState(null)
  const [cityMapUiLang, setCityMapUiLang] = useState(null)

  const { id } = useParams()
  const history = useHistory()
  const location = useLocation()
  const userFromStore = useSelector(state => get(state, 'user.user'))
  // eslint-disable-next-line
  const hasAdminAccess = userFromStore?.roles?.some(role => role.type === 'admin')

  const tabs = [
    { id: 1, name: t('masterData.tabs.vehicleMake'), key: 'make' },
    { id: 2, name: t('masterData.tabs.vehicleModel'), key: 'model' },
    { id: 13, name: t('masterData.tabs.vehicleModelImages'), key: 'vehicleModelImages' },
    { id: 3, name: t('masterData.tabs.vehicleColor'), key: 'color' },
    { id: 4, name: t('masterData.tabs.vehicleFuelPolicy'), key: 'fuelPolicy' },
    { id: 14, name: t('masterData.tabs.countries'), key: 'countries' },
    { id: 12, name: t('masterData.tabs.cities'), key: 'cities' },
    { id: 15, name: t('masterData.tabs.pointOfInterest'), key: 'pointOfInterest' },
    { id: 5, name: t('masterData.tabs.vehicleInsurance'), key: 'insurance' },
    { id: 6, name: t('masterData.tabs.marketSegment'), key: 'marketSegment' },
    { id: 7, name: t('masterData.tabs.driveWheel'), key: 'driveWheel' },
    // Replaced by the Refund Engine tab (16).
    // { id: 8, name: t('masterData.tabs.refundPolicies'), key: 'refundPolicies' },
    { id: 16, name: t('masterData.tabs.refundEngine'), key: 'refundEngine' },
    { id: 9, name: t('masterData.tabs.ajarleeConfig'), key: 'config' },
    { id: 10, name: t('masterData.tabs.notes'), key: 'notes' },
    { id: 11, name: t('masterData.tabs.imageManagement'), key: 'imageManagement' },
  ]

  const configTypeOptions = [
    { id: 'percentage', name: t('masterData.configTypes.percentage') },
    { id: 'fixed', name: t('masterData.configTypes.fixed') },
  ]

  const configValidationSchema = yup.object().shape({
    deliveryAmountType: yup
      .string()
      .oneOf(['fixed', 'percentage'], t('masterData.validation.deliveryAmountTypeInvalid'))
      .required(t('masterData.validation.deliveryAmountTypeRequired')),
    deliveryAmount: yup
      .number()
      .required(t('masterData.validation.deliveryAmountRequired'))
      .positive(t('masterData.validation.deliveryAmountPositive'))
      .when('deliveryAmountType', {
        is: 'percentage',
        then: yup.number().max(100, t('masterData.validation.percentageExceeded')),
        otherwise: yup.number(),
      }),
    bookingConfirmationTime: yup
      .number()
      .transform((value, originalValue) => (originalValue === '' || originalValue == null ? 0 : value))
      .min(0)
      .integer(),
  })

  const fetchCarJsonData = async () => {
    setCarJsonLoading(true)
    try {
      const res = await apiGet('cars-json', {})
      if (res?.success && res?.data?.rentalCars) {
        setCarJsonData(res.data.rentalCars)
      }
    } catch (err) {
      console.error('Error fetching car JSON data:', err)
    } finally {
      setCarJsonLoading(false)
    }
  }

  useEffect(() => {
    fetchCurrentTabData()
  }, [currentTab, search, sort])

  useEffect(() => {
    if (previousConfigType && configType && previousConfigType !== configType) {
      setConfigValue('0')
    }
    setPreviousConfigType(configType)
  }, [configType, previousConfigType])

  useEffect(() => {
    fetchCarJsonData()
  }, [])

  const fetchAjarleeConfig = async () => {
    try {
      setLoading(true)
      const response = await apiGet('ajarlee-config')

      if (response?.success && response?.data) {
        setConfigType(response.data.deliveryAmountType || '')
        setConfigValue(response.data.deliveryAmount || '')
        setFromTime(response.data.fromTime || '')
        setToTime(response.data.toTime || '')
        setStatus(response.data.status || false)
        // eslint-disable-next-line
        setBookingConfirmationTime(typeof response.data.bookingConfirmationTime === 'number' ? response.data.bookingConfirmationTime : 0)
        setCheckoutRejectAfterConfirmationExpiry(response.data.checkoutRejectAfterConfirmationExpiry === true)
        setHideOlderModelYears(response.data.hideOlderModelYears === true)
        setConfigExists(true)
      } else {
        setConfigType('')
        setConfigValue('')
        setFromTime('')
        setToTime('')
        setStatus(false)
        setBookingConfirmationTime(0)
        setCheckoutRejectAfterConfirmationExpiry(false)
        setHideOlderModelYears(false)
        setConfigExists(false)
      }
    } catch (error) {
      setConfigType('')
      setConfigValue('')
      setFromTime('')
      setToTime('')
      setStatus(false)
      setBookingConfirmationTime(0)
      setCheckoutRejectAfterConfirmationExpiry(false)
      setHideOlderModelYears(false)
      setConfigExists(false)
    } finally {
      setLoading(false)
    }
  }

  const fetchCurrentTabData = async (page = 1) => {
    // Point of interest and the refund engine load their own data.
    if (currentTab === 15 || currentTab === 16) {
      setLoading(false)
      return
    }
    if (page === 1) {
      setLoading(true)
    }

    if (currentTab === 9) {
      await fetchAjarleeConfig()
      return
    }

    const params = {
      pageNumber: page,
      pageSize: 100,
      search: search || undefined,
      sortBy: sort.sortBy || undefined,
      sortOrder: sort.sortOrder || undefined,
    }

    try {
      let response
      let dataKey
      let setterFunction

      switch (currentTab) {
        case 1:
          response = await apiGet('makes', params)
          dataKey = 'makes'
          setterFunction = setMakes
          break
        case 2:
          response = await apiGet('modals', params)
          dataKey = 'modals'
          setterFunction = setModels
          break
        case 13:
          response = await apiGet('vehicle-model-profiles', params)
          dataKey = 'vehicleModelProfiles'
          setterFunction = setVehicleModelProfiles
          break
        case 3:
          response = await apiGet('colors', params)
          dataKey = 'colors'
          setterFunction = setColors
          break
        case 4:
          response = await apiGet('fuel-policies', params)
          dataKey = 'fuelPolicy'
          setterFunction = setFuelPolicy
          break
        case 5:
          response = await apiGet('insurances', params)
          dataKey = 'insurance'
          setterFunction = setInsurances
          break
        case 6:
          response = await apiGet('market-segments', params)
          dataKey = 'marketSegment'
          setterFunction = setMarketSegments
          break
        case 7:
          response = await apiGet('drive-wheels', params)
          dataKey = 'driveWheel'
          setterFunction = setDriveWheels
          break
        case 8:
          response = await apiGet('refund-policy-contents', params)
          dataKey = 'refundPolicyContents'
          setterFunction = setRefundPolicyContents
          break
        case 10:
          response = await apiGet('notes', params)
          dataKey = 'notes'
          setterFunction = setNotes
          break
        case 11:
          response = await apiGet('app-banners', params)
          dataKey = 'appBanners'
          setterFunction = setAppBanners
          apiGet('booking-countries').then(countriesResponse => {
            setBookingCountries(countriesResponse?.data?.countries || [])
          })
          break
        case 12:
          response = await apiGet('cities', params)
          dataKey = 'cities'
          setterFunction = setCities
          break
        case 14: {
          const countriesResponse = await apiGet('countries', params)
          if (countriesResponse?.data) {
            const newCountries = countriesResponse.data.countries || []
            if (page === 1) {
              setCountries(newCountries)
            } else {
              setCountries(prev => [...prev, ...newCountries])
            }
            setPagination(countriesResponse?.data?.pagination || {})
          }
          setLoading(false)
          return
        }
        case 15:
          setLoading(false)
          return
        default:
          setLoading(false)
          return
      }

      if (response?.data) {
        const newData = response.data[dataKey] || []
        setterFunction(page === 1 ? newData : [...getCurrentTabData(), ...newData])
        setPagination(response?.data?.pagination || {})
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    }

    setLoading(false)
  }

  const getCurrentTabData = () => {
    switch (currentTab) {
      case 1:
        return makes
      case 2:
        return models
      case 13:
        return vehicleModelProfiles
      case 3:
        return colors
      case 4:
        return fuelPolicies
      case 5:
        return insurances
      case 6:
        return marketSegments
      case 7:
        return driveWheels
      case 8:
        return refundPolicyContents
      case 9:
        return []
      case 10:
        return notes
      case 11:
        return appBanners
      case 12:
        return cities
      case 14:
        return countries
      default:
        return []
    }
  }

  const handleCloseMakeModal = () => {
    setSelectedItemId(null)
  }

  const handleCloseModelModal = () => {
    setSelectedItemId(null)
  }

  const handleCloseVehicleModelProfileModal = () => {
    setSelectedItemId(null)
  }

  const handleCloseColorModal = () => {
    setSelectedItemId(null)
  }
  const handleCloseFuelPolicyModal = () => {
    setSelectedItemId(null)
  }

  const handleCloseInsuranceModal = () => {
    setSelectedItemId(null)
  }
  const handleCloseMarketSegmentModal = () => {
    setSelectedItemId(null)
  }
  const handleCloseDriveWheelsModal = () => {
    setSelectedItemId(null)
  }

  const handleCloseNoteModal = () => {
    setSelectedItemId(null)
  }

  const handleCloseCityModal = () => {
    setSelectedItemId(null)
    setCityMapUiLang(null)
  }

  const handleCloseCountryModal = () => {
    setSelectedItemId(null)
  }

  const handleAddConfig = async () => {
    setValidationErrors({})

    try {
      await configValidationSchema.validate(
        {
          deliveryAmountType: configType,
          deliveryAmount: parseFloat(configValue) || 0,
          bookingConfirmationTime: Number(bookingConfirmationTime),
        },
        { abortEarly: false }
      )
    } catch (validationError) {
      const errors = {}
      // eslint-disable-next-line
      validationError.inner.forEach(error => {
        errors[error.path] = error.message
      })
      setValidationErrors(errors)
      notify.error(t('masterData.messages.validationError'))
      return
    }

    try {
      setLoading(true)
      let response

      if (configExists) {
        response = await apiPut('ajarlee-config', {
          deliveryAmountType: configType,
          deliveryAmount: configValue,
          fromTime,
          toTime,
          status,
          bookingConfirmationTime: Number(bookingConfirmationTime) || 0,
          checkoutRejectAfterConfirmationExpiry,
          hideOlderModelYears,
        })
      } else {
        response = await apiPost('ajarlee-config', {
          deliveryAmountType: configType,
          deliveryAmount: configValue,
          fromTime,
          toTime,
          status,
          bookingConfirmationTime: Number(bookingConfirmationTime) || 0,
          checkoutRejectAfterConfirmationExpiry,
          hideOlderModelYears,
        })
      }

      if (response?.success) {
        const message = configExists ? t('masterData.messages.configUpdated') : t('masterData.messages.configCreated')
        notify.success(message)
        setConfigExists(true)
        setValidationErrors({})
        await fetchAjarleeConfig()
      }
    } catch (error) {
      console.error('Error saving configuration:', error)
      const errorMessage = configExists ? t('masterData.messages.configUpdateFailed') : t('masterData.messages.configSaveFailed')
      notify.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleResetConfig = () => {
    notify.deleteConfirm({
      title: t('masterData.config.resetTitle'),
      message: t('masterData.config.resetMessage'),
      onConfirm: async () => {
        try {
          setLoading(true)
          const response = await apiPut('ajarlee-config/reset')

          if (response?.success) {
            notify.success(t('masterData.messages.configReset'))
            await fetchAjarleeConfig()
          }
        } catch (error) {
          console.error('Error resetting configuration:', error)
          notify.error(t('masterData.messages.configResetFailed'))
        } finally {
          setLoading(false)
        }
      },
    })
  }

  const [MakeModal, openMakeModal, _closeMakeModal] = useModal({
    content: <AddMakeModal onSuccess={fetchCurrentTabData} id={selectedItemId} close={handleCloseMakeModal} />,
    title: selectedItemId ? t('masterData.modals.editVehicleMake') : t('masterData.modals.addNewVehicleMake'),
    dimensions: {
      height: 'auto',
      width: '90vw',
      maxWidth: '500px',
      minHeight: 'auto',
      minWidth: '280px',
    },
  })

  const [ModelModal, openModelModal, _closeModelModal] = useModal({
    content: <AddModelModal onSuccess={fetchCurrentTabData} id={selectedItemId} close={handleCloseModelModal} />,
    title: selectedItemId ? t('masterData.modals.editVehicleModel') : t('masterData.modals.addNewVehicleModel'),
    dimensions: {
      height: 'auto',
      width: '90vw',
      maxWidth: '500px',
      minHeight: 'auto',
      minWidth: '280px',
    },
  })

  const [VehicleModelProfileModal, openVehicleModelProfileModal, _closeVehicleModelProfileModal] = useModal({
    content: <AddVehicleModelProfileModal onSuccess={fetchCurrentTabData} id={selectedItemId} close={handleCloseVehicleModelProfileModal} />,
    title: selectedItemId ? t('masterData.modals.editVehicleModelImage') : t('masterData.modals.addNewVehicleModelImage'),
    dimensions: {
      height: 'auto',
      width: '90vw',
      maxWidth: '860px',
      minHeight: 'auto',
      minWidth: '320px',
      maxHeight: '90vh',
    },
  })

  const [ColorModal, openColorModal, _closeColorModal] = useModal({
    content: <AddColorModal onSuccess={fetchCurrentTabData} id={selectedItemId} close={handleCloseColorModal} />,
    title: selectedItemId ? t('masterData.modals.editVehicleColor') : t('masterData.modals.addNewVehicleColor'),
    dimensions: {
      height: 'auto',
      width: '90vw',
      maxWidth: '500px',
      minHeight: 'auto',
      minWidth: '280px',
    },
  })
  const [FuelPolicyModal, openFuelPolicyModal, _closeFuelPolicyModal] = useModal({
    content: <AddFuelPolicyModal onSuccess={fetchCurrentTabData} id={selectedItemId} close={handleCloseFuelPolicyModal} />,
    title: selectedItemId ? t('masterData.modals.editFuelPolicy') : t('masterData.modals.addNewFuelPolicy'),
    dimensions: {
      height: 'auto',
      width: '90vw',
      maxWidth: '500px',
      minHeight: 'auto',
      minWidth: '280px',
    },
  })
  const [InsuranceModal, openInsuranceModal, _closeInsuranceModal] = useModal({
    content: <AddInsuranceModal onSuccess={fetchCurrentTabData} id={selectedItemId} close={handleCloseInsuranceModal} />,
    title: selectedItemId ? t('masterData.modals.editInsurance') : t('masterData.modals.addNewInsurance'),
    dimensions: {
      height: 'auto',
      width: '90vw',
      maxWidth: '500px',
      minHeight: 'auto',
      minWidth: '280px',
    },
  })

  const [MarketSegmentModal, openMarketSegmentModal, _closeMarketSegmentModal] = useModal({
    content: <AddMarketSegmentModal onSuccess={fetchCurrentTabData} id={selectedItemId} close={handleCloseMarketSegmentModal} />,
    title: selectedItemId ? t('masterData.modals.editMarketSegment') : t('masterData.modals.addNewMarketSegment'),
    dimensions: {
      height: 'auto',
      width: '90vw',
      maxWidth: '500px',
      minHeight: 'auto',
      minWidth: '280px',
    },
  })
  const [DriveWheelModal, openDriveWheelModal, _closeDriveWheelModal] = useModal({
    content: <AddDriveWheelModal onSuccess={fetchCurrentTabData} id={selectedItemId} close={handleCloseDriveWheelsModal} />,
    title: selectedItemId ? t('masterData.modals.editDriveWheel') : t('masterData.modals.addNewDriveWheel'),
    dimensions: {
      height: 'auto',
      width: '90vw',
      maxWidth: '500px',
      minHeight: 'auto',
      minWidth: '280px',
    },
  })

  const [CarJsonModal, openCarJsonModal, _closeCarJsonModal] = useModal({
    content: <CarJson data={carJsonData} loading={carJsonLoading} />,
    title: t('masterData.modals.carJson'),
    dimensions: {
      height: 'auto',
      width: '95vw',
      maxWidth: '800px',
      minWidth: '320px',
    },
  })

  const [NoteModal, openNoteModal, _closeNoteModal] = useModal({
    content: <AddNoteModal onSuccess={fetchCurrentTabData} id={selectedItemId} close={handleCloseNoteModal} />,
    title: selectedItemId ? t('masterData.modals.editNote') : t('masterData.modals.addNewNote'),
    dimensions: {
      height: 'auto',
      width: '90vw',
      maxWidth: '600px',
      minHeight: 'auto',
      minWidth: '320px',
    },
  })

  const [CityModal, openCityModal, _closeCityModal] = useModal({
    content: <AddCityModal onSuccess={fetchCurrentTabData} id={selectedItemId} close={handleCloseCityModal} onMapUiLanguageChange={setCityMapUiLang} />,
    title:
      cityMapUiLang === 'ar'
        ? i18n.getFixedT('ar')(selectedItemId ? 'masterData.modals.editCity' : 'masterData.modals.addNewCity')
        : selectedItemId
        ? t('masterData.modals.editCity')
        : t('masterData.modals.addNewCity'),
    dimensions: {
      height: 'auto',
      width: '90vw',
      maxWidth: '680px',
      minHeight: 'auto',
      minWidth: '320px',
      maxHeight: '90vh',
    },
  })

  const [CountryModal, openCountryModal, _closeCountryModal] = useModal({
    content: <AddCountryModal onSuccess={fetchCurrentTabData} id={selectedItemId} close={handleCloseCountryModal} />,
    title: selectedItemId ? t('masterData.modals.editCountry') : t('masterData.modals.addNewCountry'),
    dimensions: {
      height: 'auto',
      width: '90vw',
      maxWidth: '500px',
      minHeight: 'auto',
      minWidth: '280px',
      maxHeight: '90vh',
    },
  })

  const getColumns = () => {
    if (currentTab === 13) {
      return [
        {
          name: t('masterData.columns.image'),
          key: 'image',
          rendered: item => {
            const imageCount = item.carImages?.length || (item.thumbnailUrl || item.url ? 1 : 0)
            return (
              <div className="flex items-center gap-2">
                <img
                  src={item.thumbnailUrl || item.url || item.carImages?.[0]?.thumbnailUrl || item.carImages?.[0]?.url}
                  alt={`${item.make?.make || ''} ${item.model?.modal || ''} ${item.modelYear || ''}`}
                  className="h-12 w-20 object-cover rounded border border-gray-200"
                />
                {imageCount > 1 ? <span className="text-xs text-gray-500">+{imageCount - 1}</span> : null}
              </div>
            )
          },
        },
        {
          name: t('masterData.columns.make'),
          key: 'make',
          rendered: item => <div className="text-sm text-gray-900">{item.make?.make || '-'}</div>,
        },
        {
          name: t('masterData.columns.model'),
          key: 'model',
          rendered: item => <div className="text-sm text-gray-900">{item.model?.modal || '-'}</div>,
        },
        {
          name: t('masterData.columns.modelYear'),
          key: 'modelYear',
          rendered: item => <div className="text-sm text-gray-900">{item.modelYear || '-'}</div>,
        },
        {
          name: t('masterData.columns.hasRealImages'),
          key: 'hasRealImages',
          rendered: item => (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                item.hasRealImages ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'
              }`}
            >
              {item.hasRealImages ? t('masterData.status.yes') : t('masterData.status.no')}
            </span>
          ),
        },
        {
          name: t('masterData.columns.status'),
          key: 'active',
          rendered: item => (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${item.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {item.active ? t('masterData.status.active') : t('masterData.status.inactive')}
            </span>
          ),
        },
        {
          name: t('masterData.columns.createdAt'),
          key: 'created',
          rendered: item => <div className="text-sm text-gray-500">{moment(get(item, 'createdAt', '')).fromNow()}</div>,
        },
        {
          name: t('masterData.columns.updatedAt'),
          key: 'updated',
          rendered: item => <div className="text-sm text-gray-500">{moment(get(item, 'updatedAt', '')).fromNow()}</div>,
        },
        {
          width: '5%',
          name: t('masterData.columns.actions'),
          key: 'actions',
          rendered: item => (
            <div className="flex space-x-4">
              <IoPencil className="text-dark-600 cursor-pointer" onClick={() => handleEdit(item)} />
              <IoTrashBin className="text-red-500 cursor-pointer" onClick={() => handleDelete(item)} />
            </div>
          ),
        },
      ]
    }

    if (currentTab === 11) {
      return [
        {
          name: t('masterData.columns.image'),
          key: 'image',
          rendered: item => {
            const bannerImage = isRTL ? item.imageAr || item.image : item.image || item.imageAr
            return <img src={bannerImage} alt={item.title || 'banner'} className="h-12 w-20 object-cover rounded border border-gray-200" />
          },
        },
        {
          name: t('masterData.columns.title'),
          key: 'title',
          rendered: item => <div className="text-sm text-gray-900">{item.title || '-'}</div>,
        },
        {
          name: t('masterData.columns.bannerType'),
          key: 'bannerType',
          rendered: item => (
            <div className="text-sm text-gray-900">
              {item.bannerType === 'carDetail' ? t('masterData.bannerTypes.carDetail') : item.bannerType === 'carListing' ? t('masterData.bannerTypes.carListing') : '-'}
            </div>
          ),
        },
        {
          name: t('masterData.columns.domains'),
          key: 'domains',
          rendered: item => {
            const domains = isArray(item.domains) && item.domains.length ? item.domains : ['om']
            const labels = domains.map(domain => {
              const match = find(bookingCountries, ['code', domain])
              if (match) return isRTL ? match.nameAr || match.name : match.name
              return toUpper(domain)
            })
            return <div className="text-sm text-gray-900">{labels.join(', ')}</div>
          },
        },
        {
          name: t('masterData.columns.linkUrl'),
          key: 'linkUrl',
          rendered: item => {
            if (!item.linkUrl) {
              return <span className="text-sm text-gray-400">-</span>
            }
            const copyLink = () => {
              navigator.clipboard.writeText(item.linkUrl)
              notify.success(t('masterData.messages.linkCopied'))
            }
            return (
              <div className="flex items-center gap-2 max-w-xs">
                <a href={item.linkUrl} target="_blank" rel="noreferrer" className="text-sm text-dark-600 hover:underline truncate" title={item.linkUrl}>
                  {startEndTruncate(item.linkUrl, 28)}
                </a>
                <button type="button" onClick={copyLink} className="text-gray-500 hover:text-gray-700 shrink-0">
                  <IoCopyOutline className="h-4 w-4" />
                </button>
              </div>
            )
          },
        },
        {
          name: t('masterData.columns.effectiveStartDate'),
          key: 'effectiveStartDate',
          rendered: item => {
            const startDate = get(item, 'effectiveStartDate', '')
            const displayStartDate = startDate ? moment(startDate).fromNow() : 'N/A'
            return <div className="text-sm text-gray-500">{displayStartDate}</div>
          },
        },
        {
          name: t('masterData.columns.effectiveEndDate'),
          key: 'effectiveEndDate',
          rendered: item => {
            const endDate = get(item, 'effectiveEndDate', '')
            const displayEndDate = endDate ? moment(endDate).fromNow() : t('masterData.messages.noEndDate')
            return <div className="text-sm text-gray-500">{displayEndDate}</div>
          },
        },
        {
          name: t('masterData.columns.status'),
          key: 'active',
          rendered: item => (
            <div className="flex items-center gap-2">
              <Switch
                value={item.active}
                onChange={async checked => {
                  const response = await apiPut(`app-banner/${item._id}`, {
                    active: checked,
                  })
                  if (response?.success) {
                    setAppBanners(prev => prev.map(row => (row._id === item._id ? { ...row, active: checked } : row)))
                  }
                }}
              />
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${item.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
              >
                {item.active ? t('masterData.status.active') : t('masterData.status.inactive')}
              </span>
            </div>
          ),
        },
        {
          name: t('masterData.columns.createdAt'),
          key: 'created',
          rendered: item => <div className="text-sm text-gray-500">{moment(get(item, 'createdAt', '')).fromNow()}</div>,
        },
        {
          name: t('masterData.columns.updatedAt'),
          key: 'updated',
          rendered: item => <div className="text-sm text-gray-500">{moment(get(item, 'updatedAt', '')).fromNow()}</div>,
        },
        {
          width: '5%',
          name: t('masterData.columns.actions'),
          key: 'actions',
          rendered: item => (
            <div className="flex space-x-4">
              <IoPencil className="text-dark-600 cursor-pointer" onClick={() => handleEdit(item)} />
              <IoTrashBin className="text-red-500 cursor-pointer" onClick={() => handleDelete(item)} />
            </div>
          ),
        },
      ]
    }

    const plainText = html =>
      // eslint-disable-next-line
      (html || '')
        .replace(/<[^>]+>/g, '')
        .replace(/\s+/g, ' ')
        .trim()

    const truncateText = text => {
      if (!text) return ''
      const maxLength = 120
      return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text
    }

    const baseColumns = [
      {
        name: currentTab === 10 ? t('masterData.columns.notes') : t('masterData.columns.name'),
        width: '10%',
        key: 'name',
        sortable: true,
        rendered: item => {
          let displayName = ''
          switch (currentTab) {
            case 1:
              displayName = item.make
              break
            case 2:
              displayName = item.modal
              break
            case 3:
              displayName = (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 rounded border border-gray-300" style={{ backgroundColor: item.hex }}></div>
                  <span>{item.color}</span>
                  <span className="text-gray-500">({item.hex})</span>
                </div>
              )
              break
            case 4:
              displayName = item.fuelPolicy
              break
            case 5:
              displayName = item.insurance
              break
            case 6:
              displayName = item.marketSegment
              break
            case 7:
              displayName = item.driveWheel
              break
            case 8:
              displayName = truncateText(plainText(item.content))
              break
            case 10:
              displayName = item.notes
              break
            case 12:
              displayName = item.city
              break
            case 14:
              displayName = item.country
              break
          }
          return <div className="text-sm text-normal text-gray-900">{displayName}</div>
        },
      },
      {
        name: currentTab === 10 ? t('masterData.columns.arabicNotes') : t('masterData.columns.arabicName'),
        width: '10%',
        key: currentTab === 10 ? 'arabicNotes' : 'arabicName',
        sortable: true,
        rendered: item => {
          let arabicName = ''
          switch (currentTab) {
            case 1:
              arabicName = item.makeAr || ''
              break
            case 2:
              arabicName = item.modalAr || ''
              break
            case 3:
              arabicName = item.colorAr || ''
              break
            case 4:
              arabicName = item.fuelPolicyAr || ''
              break
            case 5:
              arabicName = item.insuranceAr || ''
              break
            case 6:
              arabicName = item.marketSegmentAr || ''
              break
            case 7:
              arabicName = item.driveWheelAr || ''
              break
            case 8:
              arabicName = truncateText(plainText(item.contentAr))
              break
            case 10:
              arabicName = item.notesAr || ''
              break
            case 12:
              arabicName = item.cityAr || ''
              break
            case 14:
              arabicName = item.countryAr || ''
              break
          }
          return (
            <div className="text-sm text-normal text-gray-900" dir="rtl">
              {arabicName || ''}
            </div>
          )
        },
      },

      ...(currentTab === 2
        ? [
            {
              name: t('masterData.columns.marketSegment'),
              key: 'marketSegment',
              sortable: true,
              rendered: item => (
                <div className="text-sm text-gray-900">
                  {isRTL && item.marketSegment?.marketSegmentAr ? item.marketSegment.marketSegmentAr : item.marketSegment?.marketSegment || '-'}
                </div>
              ),
            },
          ]
        : []),
      ...(currentTab === 12
        ? [
            {
              name: t('masterData.columns.country'),
              key: 'country',
              sortable: true,
              rendered: item => <div className="text-sm text-gray-900">{isRTL && item.country?.countryAr ? item.country.countryAr : item.country?.country || '-'}</div>,
            },
            {
              name: t('masterData.columns.latitude'),
              key: 'lat',
              sortable: true,
              rendered: item => <div className="text-sm text-gray-900">{item.lat != null ? item.lat : '-'}</div>,
            },
            {
              name: t('masterData.columns.longitude'),
              key: 'lon',
              sortable: true,
              rendered: item => <div className="text-sm text-gray-900">{item.lon != null ? item.lon : '-'}</div>,
            },
          ]
        : []),
      {
        name: currentTab === 10 ? t('masterData.columns.notesFor') : t('masterData.columns.status'),
        key: currentTab === 10 ? 'notesFor' : 'active',
        sortable: true,
        rendered: item => {
          if (currentTab === 10) {
            return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{item.notesFor || 'N/A'}</span>
          }
          return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${item.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {item.active ? t('masterData.status.active') : t('masterData.status.inactive')}
            </span>
          )
        },
      },
      {
        name: t('masterData.columns.createdAt'),
        key: 'created',
        sortable: true,
        rendered: item => {
          const createdTime = get(item, 'createdAt', '')
          const createdAt = createdTime ? moment(createdTime).fromNow() : 'N/A'
          return <div className="text-sm text-gray-500">{createdAt}</div>
        },
      },
      {
        name: t('masterData.columns.updatedAt'),
        key: 'updated',
        sortable: true,
        rendered: item => {
          const updatedTime = get(item, 'updatedAt', '')
          const updatedAt = updatedTime ? moment(updatedTime).fromNow() : 'N/A'
          return <div className="text-sm text-gray-500">{updatedAt}</div>
        },
      },

      {
        width: '5%',
        name: t('masterData.columns.actions'),
        key: 'actions',
        rendered: item => (
          <div className="flex space-x-4">
            <IoPencil className="text-dark-600 cursor-pointer" onClick={() => handleEdit(item)} />

            <IoTrashBin className="text-red-500 cursor-pointer" onClick={() => handleDelete(item)} />
          </div>
        ),
      },
    ]

    return baseColumns
  }

  const handleEdit = item => {
    setSelectedItemId(item._id)
    switch (currentTab) {
      case 1:
        openMakeModal()
        break
      case 2:
        openModelModal()
        break
      case 13:
        openVehicleModelProfileModal()
        break
      case 3:
        openColorModal()
        break
      case 4:
        openFuelPolicyModal()
        break
      case 5:
        openInsuranceModal()
        break
      case 6:
        openMarketSegmentModal()
        break
      case 7:
        openDriveWheelModal()
        break
      case 8:
        history.push(`/master-data/refund-policy-content/edit/${item._id}`)
        break
      case 10:
        openNoteModal()
        break
      case 11:
        history.push(`/master-data/app-banner/edit/${item._id}`)
        break
      case 12:
        openCityModal()
        break
      case 14:
        openCountryModal()
        break
      default:
        break
    }
  }

  const handleDelete = item => {
    // eslint-disable-next-line
    const currentTabData = tabs.find(tab => tab.id === currentTab)
    const itemType = currentTabData?.name || 'item'

    let endpoint = ''
    switch (currentTab) {
      case 1:
        endpoint = `make/${item._id}`
        break
      case 2:
        endpoint = `modal/${item._id}`
        break
      case 13:
        endpoint = `vehicle-model-profile/${item._id}`
        break
      case 3:
        endpoint = `color/${item._id}`
        break
      case 4:
        endpoint = `fuel-policy/${item._id}`
        break
      case 5:
        endpoint = `insurance/${item._id}`
        break
      case 6:
        endpoint = `market-segment/${item._id}`
        break
      case 7:
        endpoint = `drive-wheel/${item._id}`
        break
      case 8:
        endpoint = `refund-policy-content/${item._id}`
        break
      case 10:
        endpoint = `notes/${item._id}`
        break
      case 11:
        endpoint = `app-banner/${item._id}`
        break
      case 12:
        endpoint = `city/${item._id}`
        break
      case 14:
        endpoint = `country/${item._id}`
        break
      default:
        return
    }

    notify.deleteConfirm({
      title: t('masterData.modals.deleteItem', { itemType }),
      // eslint-disable-next-line
      message: t('masterData.modals.deleteConfirm', { itemType: itemType.toLowerCase() }),
      onConfirm: async () => {
        const response = await apiDelete(endpoint)
        if (response?.success) {
          fetchCurrentTabData()
          notify.success(t('masterData.messages.itemDeleted', { itemType }))
        }
      },
    })
  }

  const handleAddNew = () => {
    setSelectedItemId(null)
    switch (currentTab) {
      case 1:
        openMakeModal()
        break
      case 2:
        openModelModal()
        break
      case 13:
        openVehicleModelProfileModal()
        break
      case 3:
        openColorModal()
        break
      case 4:
        openFuelPolicyModal()
        break
      case 5:
        openInsuranceModal()
        break
      case 6:
        openMarketSegmentModal()
        break
      case 7:
        openDriveWheelModal()
        break
      case 8:
        history.push('/master-data/refund-policy-content/add')
        break
      case 10:
        openNoteModal()
        break
      case 11:
        history.push('/master-data/app-banner/add')
        break
      case 12:
        openCityModal()
        break
      case 14:
        openCountryModal()
        break
      default:
        break
    }
  }

  const getCurrentTabName = () => {
    // eslint-disable-next-line
    return tabs.find(tab => tab.id === currentTab)?.name || 'Data'
  }

  const getAddButtonText = () => {
    switch (currentTab) {
      case 1:
        return t('masterData.buttons.addVehicleMake')
      case 2:
        return t('masterData.buttons.addVehicleModel')
      case 13:
        return t('masterData.buttons.addVehicleModelImage')
      case 3:
        return t('masterData.buttons.addVehicleColor')
      case 4:
        return t('masterData.buttons.addFuelPolicy')
      case 5:
        return t('masterData.buttons.addInsurance')
      case 6:
        return t('masterData.buttons.addMarketSegment')
      case 7:
        return t('masterData.buttons.addWheelDrive')
      case 8:
        return t('masterData.buttons.addRefundPolicies')
      case 10:
        return t('masterData.buttons.addNote')
      case 11:
        return t('masterData.buttons.addImage')
      case 12:
        return t('masterData.buttons.addCity')
      case 14:
        return t('masterData.buttons.addCountry')
      default:
        return ''
    }
  }

  useEffect(() => {
    setSearch('')
    setSort(prevSort => (prevSort.sortBy ? {} : prevSort))
  }, [currentTab])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const tabFromQuery = Number(params.get('tab'))
    if (tabFromQuery >= 1 && tabFromQuery <= 16 && tabFromQuery !== currentTab) {
      setCurrentTab(tabFromQuery)
    }
  }, [location.search])

  const handleTabChange = tabId => {
    setCurrentTab(tabId)
    history.replace(`/master-data?tab=${tabId}`)
  }

  const currentData = getCurrentTabData()

  const handleConfigTypeChange = selectedValue => {
    const newType = selectedValue?.id || ''
    setConfigType(newType)
    setValidationErrors({})
  }

  const handleConfigValueChange = e => {
    setConfigValue(e.target.value)
    setValidationErrors({})
  }

  const handleFromTimeChange = e => {
    setFromTime(e.target.value)
  }

  const handleToTimeChange = e => {
    setToTime(e.target.value)
  }

  const handleInstantBookingStatusChange = checked => {
    setStatus(checked)
  }

  return (
    <Layout
      showLogo={!!id}
      breadcrumbs={[{ name: t('masterData.title'), pathname: '/master-data' }]}
      headerRight={
        currentTab === 15 || currentTab === 16 ? null : currentTab !== 9 ? (
          <div className="flex items-center w-full justify-end sm:mt-0 sm:ml-4">
            <PageFooter currentPage={pagination.currentPage} totalPages={pagination.totalPages} totalRecords={pagination.totalRecords} />

            <div className="mt-1 relative rounded-md shadow-sm mr-4">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" aria-hidden="true">
                <IoSearch className="mr-3 h-4 w-4 text-gray-400" aria-hidden="true" />
              </div>
              <input
                onChange={e => setSearch(e.target.value)}
                value={search}
                type="text"
                name="search"
                id="search"
                className="block w-64 sm:w-72 pl-9 shadow-none sm:text-sm bg-gray-100 border-0 focus:outline-none rounded-sm"
                placeholder={`${t('common.search')} ${getCurrentTabName()}...`}
              />
            </div>

            <button
              onClick={handleAddNew}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
            >
              {getAddButtonText()}
            </button>
          </div>
        ) : (
          <div className="flex items-center w-full justify-end sm:mt-0 sm:ml-4">
            <button
              onClick={handleResetConfig}
              disabled={loading || !configExists}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('masterData.buttons.resetConfiguration')}
            </button>
          </div>
        )
      }
      title={t('masterData.title')}
    >
      <div className="flex flex-col">
        <div className="bg-white border-b border-gray-200">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="border-b border-gray-200">
              <nav className={classNames('-mb-px flex', isRTL ? 'space-x-8 space-x-reverse' : 'space-x-8')} aria-label="Tabs">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={classNames(
                      tab.id === currentTab ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                      'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm'
                    )}
                    aria-current={currentTab === tab.id ? 'page' : undefined}
                  >
                    {tab.name}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        </div>

        <div className="flex-1 flex items-stretch">
          <main className="flex-1">
            <div className="align-middle inline-block min-w-full">
              <div className="min-w-full">
                {!loading ? (
                  <>
                    {currentTab === 16 ? (
                      <RefundEngine />
                    ) : currentTab === 15 ? (
                      <PointOfInterestPage />
                    ) : currentTab === 9 ? (
                      <div className="bg-white p-6">
                        <div className="space-y-6">
                          <div className="border-b border-gray-200 pb-4 flex items-center justify-between">
                            <h2 className="text-xl font-semibold text-gray-900">{t('masterData.config.title')}</h2>

                            <button
                              type="button"
                              onClick={openCarJsonModal}
                              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
                            >
                              {t('masterData.buttons.getCarJson')}
                            </button>
                          </div>

                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="space-y-2">
                              <label htmlFor="configType" className="block text-sm font-medium text-gray-700">
                                {t('masterData.config.deliveryAmountType')}
                              </label>
                              <div className="w-full">
                                <Selection
                                  onChange={handleConfigTypeChange}
                                  title={t('masterData.config.selectConfigType')}
                                  multiple={false}
                                  list={configTypeOptions}
                                  value={configType}
                                  className="w-full"
                                />
                                {validationErrors.deliveryAmountType && <p className="mt-1 text-sm text-red-600">{validationErrors.deliveryAmountType}</p>}
                              </div>
                            </div>

                            <div className="space-y-2">
                              <label htmlFor="configValue" className="block text-sm font-medium text-gray-700">
                                {t('masterData.config.deliveryAmount')} {configType === 'percentage' && t('masterData.config.maxPercentage')}
                              </label>
                              <div className="w-full">
                                <input
                                  type="number"
                                  id="configValue"
                                  value={configValue}
                                  onChange={handleConfigValueChange}
                                  className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 ${
                                    validationErrors.deliveryAmount ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                                  }`}
                                  placeholder={configType === 'percentage' ? t('masterData.config.enterPercentage') : t('masterData.config.enterAmount')}
                                  min="0"
                                  max={configType === 'percentage' ? '100' : undefined}
                                  step={configType === 'percentage' ? '0.01' : '0.01'}
                                />
                                {validationErrors.deliveryAmount && <p className="mt-1 text-sm text-red-600">{validationErrors.deliveryAmount}</p>}
                              </div>
                            </div>
                          </div>

                          <div className="border-b border-gray-200 pb-4">
                            <div className="flex items-center justify-between">
                              <h2 className="text-xl font-semibold text-gray-900">{t('masterData.config.instantBookingTitle')}</h2>
                              <div className="flex items-center gap-3">
                                <p className="whitespace-nowrap text-sm font-medium text-gray-700">{t('masterData.config.status')}</p>
                                <Switch value={status} onChange={handleInstantBookingStatusChange} />
                              </div>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="space-y-2">
                              <label htmlFor="fromTime" className="block text-sm font-medium text-gray-700">
                                {t('masterData.config.fromTime')}
                              </label>
                              <div className="w-full">
                                <input
                                  type="time"
                                  id="fromTime"
                                  value={fromTime}
                                  onChange={handleFromTimeChange}
                                  className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 ${
                                    validationErrors.fromTime ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                                  }`}
                                />
                                {validationErrors.fromTime && <p className="mt-1 text-sm text-red-600">{validationErrors.fromTime}</p>}
                              </div>
                            </div>

                            <div className="space-y-2">
                              <label htmlFor="toTime" className="block text-sm font-medium text-gray-700">
                                {t('masterData.config.toTime')}
                              </label>
                              <div className="w-full">
                                <input
                                  type="time"
                                  id="toTime"
                                  value={toTime}
                                  onChange={handleToTimeChange}
                                  className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 ${
                                    validationErrors.toTime ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                                  }`}
                                />
                                {validationErrors.toTime && <p className="mt-1 text-sm text-red-600">{validationErrors.toTime}</p>}
                              </div>
                            </div>
                          </div>

                          <div className="border-b border-gray-200 pb-4 pt-2">
                            <h2 className="text-xl font-semibold text-gray-900">{t('masterData.config.bookingConfirmationTitle')}</h2>
                          </div>

                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="space-y-2">
                              <label htmlFor="bookingConfirmationTime" className="block text-sm font-medium text-gray-700">
                                {t('masterData.config.bookingConfirmationTime')}
                              </label>
                              <div className="w-full">
                                <input
                                  type="number"
                                  id="bookingConfirmationTime"
                                  value={bookingConfirmationTime}
                                  onChange={e => setBookingConfirmationTime(e.target.value)}
                                  min="0"
                                  step="1"
                                  className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 ${
                                    validationErrors.bookingConfirmationTime ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                                  }`}
                                  placeholder={t('masterData.config.enterBookingConfirmationTime')}
                                />
                                {validationErrors.bookingConfirmationTime && <p className="mt-1 text-sm text-red-600">{validationErrors.bookingConfirmationTime}</p>}
                              </div>
                            </div>

                            <div className="space-y-2">
                              <div className="flex items-center justify-between pt-6">
                                <label htmlFor="checkoutRejectAfterConfirmationExpiry" className="block text-sm font-medium text-gray-700">
                                  {t('masterData.config.checkoutRejectAfterConfirmationExpiry')}
                                </label>
                                <Switch value={checkoutRejectAfterConfirmationExpiry} onChange={setCheckoutRejectAfterConfirmationExpiry} />
                              </div>
                              <p className="text-xs text-gray-500">{t('masterData.config.checkoutRejectAfterConfirmationExpiryHint')}</p>
                            </div>
                          </div>

                          <div className="border-b border-gray-200 pb-4 pt-2">
                            <h2 className="text-xl font-semibold text-gray-900">{t('masterData.config.vehicleListingTitle')}</h2>
                          </div>

                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <label htmlFor="hideOlderModelYears" className="block text-sm font-medium text-gray-700">
                                {t('masterData.config.hideOlderModelYears', { minYear: LISTING_MIN_MODEL_YEAR })}
                              </label>
                              <Switch value={hideOlderModelYears} onChange={setHideOlderModelYears} />
                            </div>
                            <p className="text-xs text-gray-500">{t('masterData.config.hideOlderModelYearsHint', { minYear: LISTING_MIN_MODEL_YEAR })}</p>
                          </div>

                          <div className="flex justify-end space-x-4 border-t border-gray-200 pt-5">
                            <button
                              type="button"
                              onClick={handleAddConfig}
                              disabled={loading}
                              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {loading
                                ? configExists
                                  ? t('masterData.buttons.updating')
                                  : t('masterData.buttons.adding')
                                : configExists
                                ? t('masterData.buttons.updateConfig')
                                : t('masterData.buttons.addConfig')}
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        {currentData?.length === 0 ? (
                          <EmptyState
                            v2
                            description={t('masterData.messages.noDataFound', { itemType: getCurrentTabName() })}
                            action={
                              <button
                                onClick={handleAddNew}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
                              >
                                {getAddButtonText()}
                              </button>
                            }
                          />
                        ) : (
                          <Table
                            hasMore={pagination?.currentPage !== pagination?.totalPages}
                            fetchData={() => fetchCurrentTabData(pagination?.currentPage + 1)}
                            sortBy={sort.sortBy}
                            sortOrder={sort.sortOrder}
                            handleSort={(sortBy, sortOrder) => setSort({ sortBy, sortOrder })}
                            data={currentData}
                            columns={getColumns()}
                          />
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <div className="w-full h-[200px] border-0 flex justify-center items-center">
                    <LoadingIcon />
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>

      <MakeModal />
      <ModelModal />
      <VehicleModelProfileModal />
      <ColorModal />
      <FuelPolicyModal />
      <InsuranceModal />
      <MarketSegmentModal />
      <DriveWheelModal />
      <NoteModal />
      <CityModal />
      <CountryModal />
      <CarJsonModal />
    </Layout>
  )
}

export default MasterData
